import type {ReactNode} from 'react';
import {ChevronDown, GripVertical, Plus} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {Switch} from '@common-web/components/ui/switch';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import type {RouterOutput} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {CheckRow} from '@internal/pages/group-pipeline/components/CheckRow';
import {PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';

type TenantConfig = RouterOutput['internal']['groupPipeline']['config'][number];
export type TaskConfig = TenantConfig['tasks'][number];
type DocumentConfig = TenantConfig['documents'][number];

export const READ_ONLY = 'Read only in this first version';

export const ACTOR_LABELS: Record<TaskConfig['actor'], string> = {
  CBS: 'CBS',
  EMPLOYER: 'Employer',
  BROKER: 'Broker',
  EXTERNAL: 'External',
};

const RULES = {
  completeWhenDocsReceived: {
    tag: 'Finishes on documents',
    label: 'Finishes when its documents are received',
    detail: 'Until Documents is built, it is finished with Mark complete.',
  },
  onRejectSendBack: {
    tag: 'Sent back on reject',
    label: 'Sent back when a document is rejected',
    detail: 'Starts the next attempt. Not active until Documents is built.',
  },
} as const;
const RULE_TYPES = Object.keys(RULES) as (keyof typeof RULES)[];

const NOT_ENFORCED = 'Not enforced yet: any step can be skipped with a reason.';
const NOT_USED = 'Not used yet.';

// Shared column template so the header and every row line up.
export const STEP_GRID =
  'grid grid-cols-[16px_20px_minmax(0,1.4fr)_150px_130px_minmax(0,1fr)_16px] items-center gap-x-4';

// Disabled controls don't fire hover events, so the wrapper carries the hint and cursor.
export function ReadOnly({children, className}: {children: ReactNode; className?: string}) {
  return (
    <span title={READ_ONLY} className={cn('block cursor-not-allowed', className)}>
      {children}
    </span>
  );
}

// Disabled, but still easy to read.
const DISABLED_FIELD = 'pointer-events-none disabled:opacity-100 disabled:bg-gray-50';

function tagsFor(task: TaskConfig) {
  const tags: {label: string; hint: string; notBuilt?: boolean}[] = [];
  if (!task.isRequired) tags.push({label: 'Optional', hint: NOT_ENFORCED, notBuilt: true});
  if (task.isRepeatable) {
    tags.push({label: 'Repeatable', hint: 'Can go back for another attempt.'});
  }
  if (task.needsApproval) tags.push({label: 'Needs approval', hint: NOT_USED, notBuilt: true});
  for (const rule of task.rules) {
    tags.push({
      label: RULES[rule.type].tag,
      hint: `${RULES[rule.type].label}. ${RULES[rule.type].detail}`,
      notBuilt: true,
    });
  }
  return tags;
}

export function TaskConfigRow({
  task,
  order,
  tasks,
  sections,
  documents,
  expanded,
  onToggle,
}: {
  task: TaskConfig;
  order: number;
  tasks: TaskConfig[];
  sections: string[];
  documents: DocumentConfig[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const labelOf = (key: string) => tasks.find((t) => t.key === key)?.label ?? key;
  const waitsOn = task.dependsOn.map(labelOf);
  const waitedOnBy = tasks.filter((t) => t.dependsOn.includes(task.key)).map((t) => t.label);
  const linkedDocuments = documents.filter((d) => d.taskKey === task.key).map((d) => d.label);
  const chasable = task.actor === 'EMPLOYER' || task.actor === 'BROKER';
  const tags = tagsFor(task);

  return (
    <li className='border-b last:border-0'>
      <div
        onClick={onToggle}
        className={cn(STEP_GRID, 'cursor-pointer px-4 py-2.5 hover:bg-gray-50')}
      >
        <ReadOnly>
          <GripVertical className='h-4 w-4 text-gray-300' />
        </ReadOnly>
        <span className='text-sm text-gray-400'>{order}</span>
        <div className='min-w-0'>
          <button
            type='button'
            aria-expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className='block max-w-full truncate text-left text-sm font-medium'
          >
            {task.label}
          </button>
          {tags.length > 0 && (
            <div className='mt-1 flex flex-wrap gap-1.5'>
              {tags.map((tag) => (
                <Tooltip key={tag.label}>
                  <TooltipTrigger asChild>
                    <span className='relative inline-flex whitespace-nowrap rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700'>
                      {tag.label}
                      {tag.notBuilt && <PreviewDot />}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='max-w-64 bg-gray-900 text-white'>
                    {tag.hint}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
        <ReadOnly>
          <ActorSelect actor={task.actor} />
        </ReadOnly>
        <ReadOnly className='flex items-center gap-2'>
          <DueInput days={task.targetDays} />
        </ReadOnly>
        <span className='truncate text-sm text-gray-600' title={waitsOn.join(', ')}>
          {waitsOn.length ? waitsOn.join(', ') : 'Starts right away'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-gray-400 transition-transform', expanded && 'rotate-180')}
        />
      </div>

      {expanded && (
        <div className='grid gap-x-10 gap-y-6 border-t bg-gray-50/60 py-5 pl-[84px] pr-4 md:grid-cols-3'>
          <Column title='Basics'>
            <Field label='Name' hint='Shown on the group page, list and board.'>
              <ReadOnly>
                <Input value={task.label} disabled readOnly className={DISABLED_FIELD} />
              </ReadOnly>
            </Field>
            <Field label='Phase' hint='Groups show this phase while this is their current step.'>
              <ReadOnly>
                <DisabledSelect
                  value={task.section}
                  options={sections.map((section) => ({value: section, label: section}))}
                />
              </ReadOnly>
            </Field>
            <Field
              label='Who acts'
              hint={
                chasable
                  ? 'Next move offers a chase and a response while this step waits on them.'
                  : 'Who does the work on this step.'
              }
            >
              <ReadOnly>
                <ActorSelect actor={task.actor} />
              </ReadOnly>
            </Field>
            <Field
              label='Due'
              hint={
                task.targetDays === null
                  ? 'No due date, so it never shows as overdue.'
                  : 'Business days from when it opens. After that, the group shows as overdue.'
              }
            >
              <ReadOnly className='flex items-center gap-2'>
                <DueInput days={task.targetDays} />
              </ReadOnly>
            </Field>
          </Column>

          <Column title='Flow'>
            <Field
              label='Waits on'
              hint={
                waitsOn.length
                  ? 'It opens once all of these are done or skipped.'
                  : 'It opens as soon as the group is added.'
              }
            >
              <div className='flex flex-wrap items-center gap-1.5'>
                {waitsOn.map((label) => (
                  <span key={label} className='rounded-md border bg-white px-2 py-1 text-sm'>
                    {label}
                  </span>
                ))}
                <ReadOnly className='inline-block'>
                  <Button variant='outline' size='sm' disabled className='h-8 bg-white'>
                    <Plus className='mr-1 h-3.5 w-3.5' />
                    Add step
                  </Button>
                </ReadOnly>
              </div>
            </Field>
            <Field label='Steps waiting on it' hint='Follows from what the other steps wait on.'>
              <p className='text-sm'>{waitedOnBy.length ? waitedOnBy.join(', ') : 'None'}</p>
            </Field>
            <Field label='Documents' hint='Set on each document under Required documents.'>
              <p className='text-sm'>
                {linkedDocuments.length ? linkedDocuments.join(', ') : 'None'}
              </p>
            </Field>
          </Column>

          <Column title='Behavior'>
            <SwitchField label='Required' checked={task.isRequired} hint={NOT_ENFORCED} notBuilt />
            <SwitchField
              label='Repeatable'
              checked={task.isRepeatable}
              hint='Next move shows Passed / Failed. Failed starts the next attempt with a fresh due date.'
            />
            <SwitchField
              label='Needs approval'
              checked={task.needsApproval}
              hint={NOT_USED}
              notBuilt
            />
            <Field label='Rules' notBuilt>
              <div className='-mx-2 space-y-1'>
                {RULE_TYPES.map((type) => (
                  <ReadOnly key={type}>
                    <CheckRow
                      label={RULES[type].label}
                      checked={task.rules.some((rule) => rule.type === type)}
                      disabled
                    />
                    <p className='px-8 text-xs text-gray-500'>{RULES[type].detail}</p>
                  </ReadOnly>
                ))}
              </div>
            </Field>
          </Column>
        </div>
      )}
    </li>
  );
}

export function DisabledSelect({
  value,
  options,
}: {
  value: string;
  options: {value: string; label: string}[];
}) {
  return (
    <Select value={value} disabled>
      <SelectTrigger className={cn(DISABLED_FIELD, 'h-9')}>
        <SelectValue>{options.find((o) => o.value === value)?.label ?? '—'}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export const ACTOR_OPTIONS = Object.entries(ACTOR_LABELS).map(([value, label]) => ({value, label}));

function ActorSelect({actor}: {actor: TaskConfig['actor']}) {
  return <DisabledSelect value={actor} options={ACTOR_OPTIONS} />;
}

function DueInput({days}: {days: number | null}) {
  return (
    <>
      <Input
        type='number'
        value={days ?? ''}
        placeholder='—'
        disabled
        readOnly
        className={cn(DISABLED_FIELD, 'h-9 w-16 px-2 text-center')}
      />
      <span className='text-sm text-gray-500'>days</span>
    </>
  );
}

function Column({title, children}: {title: string; children: ReactNode}) {
  return (
    <div className='space-y-4'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{title}</p>
      {children}
    </div>
  );
}

function Field({
  label,
  hint,
  notBuilt,
  children,
}: {
  label: string;
  hint?: string;
  notBuilt?: boolean;
  children: ReactNode;
}) {
  return (
    <div className='space-y-1.5'>
      <p className='flex items-center gap-2 text-sm font-medium text-gray-700'>
        {label}
        {notBuilt && (
          <span className='inline-flex' title='Not built yet'>
            <PreviewDot inline />
          </span>
        )}
      </p>
      {children}
      {hint && <p className='text-xs text-gray-500'>{hint}</p>}
    </div>
  );
}

function SwitchField({
  label,
  checked,
  hint,
  notBuilt,
}: {
  label: string;
  checked: boolean;
  hint: string;
  notBuilt?: boolean;
}) {
  return (
    <div className='space-y-1'>
      <ReadOnly className='flex items-center justify-between gap-4'>
        <span className='flex items-center gap-2 text-sm font-medium text-gray-700'>
          {label}
          {notBuilt && <PreviewDot inline />}
        </span>
        <Switch
          checked={checked}
          disabled
          className='pointer-events-none disabled:opacity-100 data-[state=checked]:bg-gray-900'
        />
      </ReadOnly>
      <p className='text-xs text-gray-500'>{hint}</p>
    </div>
  );
}
